import { apiBaseUrl } from '../../config/env';
import { ConnectionType } from '../../types/network';

export interface NetworkUser {
  id: string;
  name: string | null;
  profession: string | null;
  city: string | null;
  photo?: string | null;
  businessName?: string | null;
  businessIndustry?: string | null;
  businessPhoto?: string | null;
  industry?: string | null;
  designation?: string | null;
}

export interface Connection {
  id: string;
  user: NetworkUser;
  connectionType: ConnectionType;
  connectedAt: string;
}

export interface ConnectionRequest {
  id: string;
  user: NetworkUser;
  requestedAt: string;
}

export interface SearchUser {
  id: string;
  name: string | null;
  profession: string | null;
  city: string | null;
  photo?: string | null;
  businessName?: string | null;
  businessIndustry?: string | null;
  businessPhoto?: string | null;
  industry?: string | null;
  designation?: string | null;
  role?: string | null;
  connectionStatus: 'connected' | 'pending' | 'none';
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export interface ConnectionStats {
  totalConnections: number;
  byType: {
    mentor: number;
    client: number;
    supplier: number;
  };
  pendingIncoming: number;
  pendingOutgoing: number;
}

/**
 * Get my network (accepted connections)
 */
export async function getMyConnections(
  token: string,
  filter?: ConnectionType,
  search?: string,
  page: number = 1,
  limit: number = 20
): Promise<PaginatedResponse<Connection>> {
  const params = new URLSearchParams();
  if (filter) params.append('filter', filter);
  if (search) params.append('search', search);
  params.append('page', page.toString());
  params.append('limit', limit.toString());

  const response = await fetch(`${apiBaseUrl}/api/network/connections?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get connections');
  }

  return response.json();
}

/**
 * Get connection requests (incoming or outgoing)
 */
export async function getConnectionRequests(
  token: string,
  type: 'incoming' | 'outgoing',
  page: number = 1,
  limit: number = 20
): Promise<PaginatedResponse<ConnectionRequest>> {
  const params = new URLSearchParams({
    type,
    page: page.toString(),
    limit: limit.toString(),
  });

  const response = await fetch(`${apiBaseUrl}/api/network/requests?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get requests');
  }

  return response.json();
}

/**
 * Search users
 */
export async function searchUsers(
  token: string,
  query: string,
  page: number = 1,
  limit: number = 20,
  filters?: { city?: string; designation?: string; industry?: string }
): Promise<PaginatedResponse<SearchUser>> {
  const params = new URLSearchParams({
    q: query,
    page: page.toString(),
    limit: limit.toString(),
  });
  
  // Add filters if provided
  if (filters) {
    if (filters.city) params.append('city', filters.city);
    if (filters.designation) params.append('designation', filters.designation);
    if (filters.industry) params.append('industry', filters.industry);
  }

  const response = await fetch(`${apiBaseUrl}/api/network/search?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to search users');
  }

  return response.json();
}

/**
 * Send connection request
 */
export async function sendConnectionRequest(
  token: string,
  toUserId: string,
  connectionType: ConnectionType
): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/api/network/requests`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ toUserId, connectionType }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to send connection request');
  }
}

/**
 * Accept connection request
 */
export async function acceptConnectionRequest(
  token: string,
  requestId: string,
  connectionType: ConnectionType
): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/api/network/requests/${requestId}/accept`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ connectionType }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to accept connection request');
  }
}

/**
 * Reject connection request
 */
export async function rejectConnectionRequest(
  token: string,
  requestId: string
): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/api/network/requests/${requestId}/reject`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reject connection request');
  }
}

/**
 * Remove connection
 */
export async function removeConnection(
  token: string,
  connectionId: string
): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/api/network/connections/${connectionId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove connection');
  }
}

/**
 * Get connection status with a specific user
 */
export async function getConnectionStatus(
  token: string,
  userId: string
): Promise<{
  status: 'connected' | 'pending' | 'none';
  connectionType?: ConnectionType;
  connectionId?: string;
}> {
  const response = await fetch(`${apiBaseUrl}/api/network/status/${userId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get connection status');
  }

  return response.json();
}

/**
 * Get network statistics
 */
export async function getConnectionStats(token: string): Promise<ConnectionStats> {
  const response = await fetch(`${apiBaseUrl}/api/network/stats`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get connection stats');
  }

  return response.json();
}
